<template>
  <div class="categories">
    <div class="page__header">
      <div class="page__title">{{ $t(`saleCategory.${"secondTitle"}`) }}</div>
      <button
        v-if="instance.proxy.$checkRolePermission('sales_category_price_create')"
        class="button button--default"
        @click="store.commit('setShowAddSecondSaleCategoryModal', true)"
      >
        {{ $t(`saleCategory.${"addBtn"}`) }}
      </button>
    </div>
 
    <div class="categories__container">
      <div class="sales box box--default">
        <div class="label label--gradient">{{ $t(`saleCategory.${"choseSaleCategory"}`) }}</div>


        <Loading v-if="salesLoading" />

        <div v-else class="sales__body">
          <div class="sales__teams" v-if="instance.proxy.$checkRolePermission('team_access')">
            <VSelect
              class="select select--default"
              :options="teams"
              :reduce="(team) => team.id"
              @update:modelValue="store.commit('setSecondSaleCategoryTeamId', $event)"
              :value="secondSaleCategoryTeamId"
              label="name"
              :placeholder="$t('saleCategory.selectTeam')" 
            />
          </div>
          <div
            @click="selectSale(sale.id)"
            class="sales__sale"
            :class="{ 'sales__sale--selected': sale.id === selectedSale?.id }"
            v-for="sale in secondSaleCategories"
            :key="sale.id"
          >
            {{ sale.name }}
          </div>
        </div>
      </div>
      <div class="prices box box--default">
        <div class="label label--gradient">{{ selectedSale?.name }}</div>

        <div class="prices__body">
          <div class="page__header">
            <div class="page__title">{{ selectedSale?.name_en }}</div>    <div v-if="instance.proxy.$checkRolePermission('sales_category_delete')" class="icon icon__delete" @click="deleteCategoryModal()"></div>
          </div>

          <div class="prices__header">
            <div class="column column--branch">
              {{ $t(`saleCategory.${"branch"}`) }}
            </div>
            <div class="column column--agent">
              {{ $t(`addSaleCategoryModal.${"secondAgent"}`) }}
            </div>
            <div class="column column--company">
              {{ $t(`saleCategory.${"agent"}`) }}
            </div>
          </div>

          <div class="prices__table">  
            <PriceRow
              v-for="price in selectedSale?.sales_category_prices || []"
              :key="price.id"
              :price="price"
              :selectSale="selectSale"
              :teamId="secondSaleCategoryTeamId"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Loading from "@/partials/Loading";
import PriceRow from "@/components/PriceCategories/PriceRow";

const store = useStore();
const router = useRouter();
const instance = getCurrentInstance();

// Reactive data
const salesLoading = ref(false);
const selectedSale = ref(null);

// Computed properties
const secondSaleCategories = computed(() => store.getters.secondSaleCategories);
const secondSaleCategoriesData = computed(() => store.getters.secondSaleCategoriesData);
const secondSaleCategoryTeamId = computed(() => store.getters.secondSaleCategoryTeamId);
const teams = computed(() => secondSaleCategoriesData.value.teams);

// Methods
const deleteCategory = async (categroyId) => {
  await store.dispatch("deleteSecondSaleCategory", categroyId);

  setTimeout(async () => {
    salesLoading.value = true;
    await store.dispatch("getSecondSaleCategoriesByTeam", selectedSale.value.team_id);
    setFirstSaleCategory();
    salesLoading.value = false;
  }, 300);
};

const setFirstSaleCategory = () => {
  if (secondSaleCategories.value.length) {
    selectedSale.value = secondSaleCategories.value[0];
  } else {
    selectedSale.value = {};
  }
};

const selectSale = (saleId) => {
  selectedSale.value = secondSaleCategories.value.find(
    (sale) => sale.id === saleId
  );
};

const deleteCategoryModal = () => {
  store.commit("setDeleteModal", {
    display: true,
    message: 'Sale category with name ' + `'${selectedSale.value.name}'`,
    action: deleteCategory,
    id: selectedSale.value.id,
  });
};

// Watchers
watch(secondSaleCategoryTeamId, async (teamId) => {
  salesLoading.value = true;
  if (teamId) {
    await store.dispatch("getSecondSaleCategoriesByTeam", teamId);
  } else {
    await store.dispatch("getSecondSaleCategories");
  }
  setFirstSaleCategory();
  salesLoading.value = false;
});

// Lifecycle
onMounted(async () => {
  salesLoading.value = true;

  if (!instance.proxy.$checkRolePermission('sales_category_price_access')) {
    router.push('/dashboard');
  } else {
    await store.dispatch("getSecondSaleCategories");
    await store.dispatch("getSecondSaleCategoriesFormData");
    setFirstSaleCategory(); 
  }

  salesLoading.value = false; 
});
</script>

<style scoped lang="scss">
.categories {
  height: 100%;

  .page__header {
    .button {
      width: 180px;
    }
    .icon__delete {
      margin-right: 30px;
    }
  }
  .title__delete {
    padding-right: 20px;
  }
  &__container {
    height: calc(100% - 48px - 20px);
    display: flex;
  }

  .sales,
  .prices {
    height: 100%;
    background: var(--bg-1);
    position: relative;
  }

  .sales__sale,
  .prices__price {
    color: var(--text-9);
    font-size: var(--medium);
    padding: 10px 20px;
    cursor: pointer;

    &:hover,
    &--selected {
      background: var(--bg-17);
    }
  }

  .sales,
  .prices {
    &__body {
      margin-top: 65px;
      padding-top: 20px;
      overflow: auto;
    }
  }

  .sales {
    width: 400px;
    margin-right: 20px;

    &__teams {
      padding: 0 20px;
      margin-bottom: 22px;
    }

    &__body {
      height: calc(100% - 65px);
    }
  }

  .prices {
    flex: 1;

    :deep(.column--branch) {
      flex: 1;
      border-right: 1px solid var(--border-6);
    }

    :deep(.column--agent) {
      flex: 4;
      text-align: right;
      padding-right: 90px;
      border-right: 1px solid var(--border-6);
    }

    :deep(.column--company) {
      flex: 2;
      padding-left: 100px;
    }

    &__body {
      padding-top: 0;
      height: calc(100% - 65px);
      overflow: initial;

      &--stretch {
        height: calc(100% - 65px);
      }
    }

    &__header {
      box-shadow: 0px 2px 4px var(--shadow-1);
      border-radius: var(--radius-1);
      display: flex;
      padding: 10px 20px;
      color: var(--text-2);
    }

    .page__header {
      padding-left: 20px;
    }

    &__table {
      height: calc(100% - 24px - 20px - 36px);
      overflow: auto;

      :deep(.column) {
        border-right: none;
        position: relative;
      }

      :deep(.column--branch) span {
        position: absolute;
        top: -8px;
        white-space: nowrap;
      }

      :deep(.column--agent) span,
      :deep(.column--company) span {
        position: relative;
      }

      :deep(.column--agent) span {
        left: -2px;
        text-decoration: underline;
        color: var(--text-10);
        cursor: pointer;
      }

      :deep(.column--company) span {
        left: 20px;
      }
    }
  }
}
</style>
